import { useQuery } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { PlayerSearchScope } from "@bw/shared/api";
import type { PlayerCard } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Draw-INDEPENDENT general player search (GET /social/players/search?q=&scope=),
 * EPIC P.1. Mirrors `useUserSearch` (Player Slice 4b) but for the RICH PlayerCard
 * search: it forwards `scope` and returns the server's PlayerCard[] VERBATIM
 * ({user_id, display_name, avatar_url, rating, rating_division, relationship}) —
 * never a client sort, never a derived relationship/rating. The card renders a
 * null rating as an honest "Unrated".
 *
 * The caller passes the DEBOUNCED, TRIMMED query. The query only RUNS when that
 * value is ≥ 2 chars (`enabled`) — the server requires q (min_length=2) and the
 * client guards a blank query (no fetch). A shorter / blank query keeps the
 * query idle (no fetch, no stale results), so the page shows its honest prompt
 * rather than a fabricated empty list. Keyed by the trimmed query + scope so each
 * distinct search gets its OWN cache slot (never colliding with `userSearch`).
 *
 * @param query  the (raw) search text — trimmed here.
 * @param scope  friends | recent | all (forwarded to the server). The `facility`
 *               scope was removed for privacy (#1657).
 */
export function usePlayerSearch(query: string, scope: PlayerSearchScope) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;
  return useQuery<PlayerCard[]>({
    queryKey: queryKeys.social.playerSearch(trimmed, scope),
    queryFn: () => socialApi.searchPlayers(trimmed, scope),
    enabled,
  });
}
